import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from '@/utils/className';
import WrapperContent from '@/components/Layout/WrapperContent';
import Chip from '@/components/Layout/Chip';

export interface SummaryAnchor {
  href: string;
  label: string;
}

interface PageSummaryNavigationProps {
  anchors: SummaryAnchor[];
  summaryStickyTop?: number;
  fallbackScrollMargin?: number;
  backgroundColor?: string;
  onSectionsMarginChange?: (margin: number) => void;
  onActiveAnchorChange?: (href: string) => void;
}

const DEFAULT_FALLBACK_MARGIN = 140;

export default function PageSummaryNavigation({
  anchors,
  summaryStickyTop = 0,
  fallbackScrollMargin = DEFAULT_FALLBACK_MARGIN,
  backgroundColor = 'white',
  onSectionsMarginChange,
  onActiveAnchorChange,
}: PageSummaryNavigationProps) {
  const validAnchors = useMemo(() => anchors.filter(anchor => Boolean(anchor?.href && anchor?.label)), [anchors]);

  const [activeAnchor, setActiveAnchor] = useState<string>(validAnchors[0]?.href || '');
  const activeAnchorRef = useRef(activeAnchor);
  const summaryBlockRef = useRef<HTMLDivElement | null>(null);
  const summaryNavRef = useRef<HTMLDivElement | null>(null);
  const [summaryHeight, setSummaryHeight] = useState(0);
  const [isSummaryPinned, setIsSummaryPinned] = useState(false);

  const anchorScrollOffset = summaryHeight ? summaryStickyTop + summaryHeight : fallbackScrollMargin;

  useEffect(() => {
    if (!validAnchors.length) return;
    if (!validAnchors.some(anchor => anchor.href === activeAnchorRef.current)) {
      const fallbackAnchor = validAnchors[0].href;
      activeAnchorRef.current = fallbackAnchor;
      setActiveAnchor(fallbackAnchor);
      onActiveAnchorChange?.(fallbackAnchor);
    }
  }, [validAnchors, onActiveAnchorChange]);

  useEffect(() => {
    activeAnchorRef.current = activeAnchor;
    if (activeAnchor) onActiveAnchorChange?.(activeAnchor);
  }, [activeAnchor, onActiveAnchorChange]);

  useEffect(() => {
    onSectionsMarginChange?.(anchorScrollOffset || fallbackScrollMargin);
  }, [anchorScrollOffset, fallbackScrollMargin, onSectionsMarginChange]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateSummaryHeight = () => {
      if (!summaryNavRef.current) return;
      setSummaryHeight(summaryNavRef.current.getBoundingClientRect().height);
    };

    updateSummaryHeight();
    window.addEventListener('resize', updateSummaryHeight);

    let resizeObserver: ResizeObserver | null = null;
    if (summaryNavRef.current && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(updateSummaryHeight);
      resizeObserver.observe(summaryNavRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateSummaryHeight);
      resizeObserver?.disconnect();
    };
  }, [validAnchors.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updatePinnedState = () => {
      if (!summaryBlockRef.current) return;
      const blockTop = summaryBlockRef.current.getBoundingClientRect().top + window.scrollY;
      setIsSummaryPinned(window.scrollY + summaryStickyTop >= blockTop);
    };

    updatePinnedState();
    window.addEventListener('scroll', updatePinnedState, { passive: true });
    window.addEventListener('resize', updatePinnedState);

    return () => {
      window.removeEventListener('scroll', updatePinnedState);
      window.removeEventListener('resize', updatePinnedState);
    };
  }, [summaryStickyTop]);

  useEffect(() => {
    if (typeof window === 'undefined' || !validAnchors.length) return;

    const availableAnchors = new Set(validAnchors.map(anchor => anchor.href));
    const updateActiveAnchorFromHash = () => {
      const { hash } = window.location;
      if (!hash || !availableAnchors.has(hash)) return;
      activeAnchorRef.current = hash;
      setActiveAnchor(hash);
    };

    updateActiveAnchorFromHash();
    window.addEventListener('hashchange', updateActiveAnchorFromHash);

    return () => window.removeEventListener('hashchange', updateActiveAnchorFromHash);
  }, [validAnchors]);

  useEffect(() => {
    if (typeof window === 'undefined' || !validAnchors.length) return;

    let ticking = false;
    const sections = validAnchors
      .map(anchor => document.querySelector<HTMLElement>(anchor.href))
      .filter((section): section is HTMLElement => Boolean(section));

    if (!sections.length) return;

    const getActiveFromScroll = () => {
      const scrollPosition = window.scrollY + anchorScrollOffset + 1;
      let currentSectionId = sections[0].id;

      for (let index = 0; index < sections.length; index += 1) {
        const section = sections[index];
        const nextSection = sections[index + 1];
        const sectionTop = section.offsetTop;
        const nextSectionTop = nextSection?.offsetTop ?? Number.POSITIVE_INFINITY;

        if (scrollPosition >= sectionTop && scrollPosition < nextSectionTop) {
          currentSectionId = section.id;
          break;
        }
      }

      const nextActive = `#${currentSectionId}`;
      if (activeAnchorRef.current !== nextActive) {
        activeAnchorRef.current = nextActive;
        setActiveAnchor(nextActive);
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', nextActive);
        }
      }
    };

    const onScrollOrResize = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          getActiveFromScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    getActiveFromScroll();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorScrollOffset, validAnchors]);

  const handleAnchorClick = useCallback(
    (event: MouseEvent<HTMLElement>, href: string) => {
      event.preventDefault();

      if (typeof window === 'undefined') return;
      const target = document.querySelector<HTMLElement>(href);
      if (!target) return;

      const prefersReducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
      activeAnchorRef.current = href;
      setActiveAnchor(href);
      window.history.replaceState(null, '', href);
    },
    []
  );

  if (!validAnchors.length) return null;

  return (
    <WrapperContent backgroundColor={backgroundColor}>
      <div ref={summaryBlockRef} className="w-full">
        <div aria-hidden style={{ height: isSummaryPinned ? summaryHeight : 0 }} />
        <div
          ref={summaryNavRef}
          className={classNames(
            'z-30 flex items-center justify-center bg-white/90 backdrop-blur-sm transition-shadow duration-150 ease-ease',
            isSummaryPinned
              ? 'fixed left-0 right-0 border-b border-mglGray900/10 shadow-[0_12px_34px_rgba(23,23,23,0.08)]'
              : 'relative border-y border-mglGray900/10'
          )}
          style={isSummaryPinned ? { top: summaryStickyTop } : undefined}>
          <div className="flex w-full justify-center px-0">
            <nav
              aria-label="Page summary"
              className={classNames(
                'flex w-full flex-wrap items-center justify-center gap-2 px-4 py-4 transition-all duration-300 phone:gap-3 tablet:grid tablet:grid-flow-row tablet:grid-cols-6 tablet:gap-4 tablet:px-0',
                isSummaryPinned && 'tablet:mx-4'
              )}>
              {validAnchors.map(anchor => {
                const isActive = activeAnchor === anchor.href;
                return (
                  <Chip
                    key={anchor.href}
                    link={anchor.href}
                    texto={anchor.label}
                    title={anchor.label}
                    chipSize="large"
                    className={classNames(
                      'chip-anchor px-4 transition-[transform,box-shadow] duration-300 ease-ease hover:translate-y-[-1px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 phone:px-6 tablet:px-9',
                      '[&_span]:text-xs [&_span]:font-semibold phone:[&_span]:text-sm',
                      isActive
                        ? 'border-0 bg-gradient-to-r from-[#2ba5ff] to-[#60d6ff] text-white shadow-[0_16px_34px_rgba(43,165,255,0.35)] hover:shadow-[0_20px_42px_rgba(43,165,255,0.45)] focus-visible:outline-[#2ba5ff] [&_span]:text-white'
                        : 'border border-mglGray900/15 bg-white text-mglGray900 shadow-[0_12px_26px_rgba(23,23,23,0.04)] hover:border-transparent hover:shadow-[0_16px_34px_rgba(23,23,23,0.08)] focus-visible:outline-mglGray200 [&_span]:text-mglGray900'
                    )}
                    onClick={event => handleAnchorClick(event, anchor.href)}
                    onFocus={() => {
                      activeAnchorRef.current = anchor.href;
                      setActiveAnchor(anchor.href);
                    }}
                  />
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </WrapperContent>
  );
}
